import { useState, useEffect, useMemo } from "react";
import { useParams, useLocation } from "wouter";
import {
  ArrowLeft,
  Building2,
  Users,
  UserCheck,
  ClipboardList,
  Plus,
  Pencil,
  Trash2,
  Save,
  X,
  Loader2,
  AlertCircle,
  CheckCircle2,
  Info,
  FileText,
  Download,
  RefreshCw,
  Clock,
  XCircle,
  FileCheck,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import SalviaLayout from "@/components/SalviaLayout";
import { useAuth } from "@/_core/hooks/useAuth";
import { EvaluationStatusBadge, EVALUATION_STATUS_CONFIG } from "@/components/EvaluationStatusBadge";
import { trpc } from "@/lib/trpc";
import { toast } from "sonner";

// ============================================================
// Types
// ============================================================

interface RequesterForm {
  name: string;
  jobTitle: string;
}

interface EvaluatorForm {
  name: string;
  jobTitle: string;
  education: string;
  professionalRegistration: string;
  specialization?: string;
  email?: string;
  phone?: string;
}

const emptyRequester: RequesterForm = { name: "", jobTitle: "" };
const emptyEvaluator: EvaluatorForm = {
  name: "",
  jobTitle: "",
  education: "",
  professionalRegistration: "",
  specialization: "",
  email: "",
  phone: "",
};

// ============================================================
// Component
// ============================================================

export default function DocumentContext() {
  const params = useParams<{ companyId: string }>();
  const companyId = Number(params.companyId);
  const [, setLocation] = useLocation();
  const { user } = useAuth();
  const utils = trpc.useUtils();

  // Admin interno e Admin de Operação Sálvia compartilham o mesmo escopo operacional de documentos.
  const canAccessGeneratedDocuments = user?.role === "admin" || user?.role === "salvia_operation_admin";
  const canGenerateDoc = canAccessGeneratedDocuments;

  // State
  const [requesterForm, setRequesterForm] = useState<RequesterForm>(emptyRequester);
  const [requesterEditing, setRequesterEditing] = useState(false);

  const [evalDialogOpen, setEvalDialogOpen] = useState(false);
  const [evalForm, setEvalForm] = useState<EvaluatorForm>(emptyEvaluator);
  const [editingEvalId, setEditingEvalId] = useState<number | null>(null);
  const [deleteEvalId, setDeleteEvalId] = useState<number | null>(null);
  const [activeTab, setActiveTab] = useState<string>("contexto");

  // Queries
  const { data: ctx, isLoading, error } = trpc.documents.getContext.useQuery(
    { companyId },
    {
      enabled: !!companyId && !isNaN(companyId),
      staleTime: 0, // V67: Force refetch on every mount to prevent cache stale data when switching companies
    }
  );

  // V67: Invalidate cache when companyId changes to ensure fresh data
  // This prevents stale data from being displayed when switching between companies
  useEffect(() => {
    if (companyId && !isNaN(companyId)) {
      utils.documents.getContext.invalidate({ companyId });
    }
  }, [companyId, utils]);

  // Sync requester form when data loads
  useEffect(() => {
    if (ctx?.requester) {
      setRequesterForm({
        name: ctx.requester.name,
        jobTitle: ctx.requester.jobTitle,
      });
    }
  }, [ctx?.requester?.name, ctx?.requester?.jobTitle]);

  // Mutations
  const upsertRequester = trpc.documents.upsertRequester.useMutation({
    onSuccess: () => {
      toast.success("Solicitante salvo com sucesso.");
      setRequesterEditing(false);
      utils.documents.getContext.invalidate({ companyId });
    },
    onError: (err: any) => toast.error(err.message),
  });

  const createEvaluator = trpc.documents.createEvaluator.useMutation({
    onSuccess: () => {
      toast.success("Avaliador adicionado com sucesso.");
      setEvalDialogOpen(false);
      setEvalForm(emptyEvaluator);
      setEditingEvalId(null);
      utils.documents.getContext.invalidate({ companyId });
    },
    onError: (err: any) => toast.error(err.message),
  });

  const updateEvaluator = trpc.documents.updateEvaluator.useMutation({
    onSuccess: () => {
      toast.success("Avaliador atualizado com sucesso.");
      setEvalDialogOpen(false);
      setEvalForm(emptyEvaluator);
      setEditingEvalId(null);
      utils.documents.getContext.invalidate({ companyId });
    },
    onError: (err: any) => toast.error(err.message),
  });

  const deleteEvaluator = trpc.documents.deleteEvaluator.useMutation({
    onSuccess: () => {
      toast.success("Avaliador excluído.");
      setDeleteEvalId(null);
      utils.documents.getContext.invalidate({ companyId });
    },
    onError: (err: any) => toast.error(err.message),
  });

  // Generated Documents
  const stableCompanyId = useMemo(() => ({ companyId }), [companyId]);
  const {
    data: generatedDocs,
    isLoading: isLoadingDocs,
    refetch: refetchDocs,
  } = trpc.documents.listGeneratedDocuments.useQuery(stableCompanyId, {
    enabled: !!companyId && !isNaN(companyId) && canAccessGeneratedDocuments,
    refetchInterval: (query) => {
      // Auto-refresh every 3s if any doc is processing
      const docs = query.state.data;
      if (docs && docs.some((d: any) => d.status === "processing")) return 3000;
      return false;
    },
  });

  const generateDocument = trpc.documents.generateDocument.useMutation({
    onSuccess: () => {
      toast.success("Geração de documento iniciada.");
      refetchDocs();
    },
    onError: (err: any) => toast.error(err.message),
  });

  // Handlers
  const handleSaveRequester = () => {
    if (!requesterForm.name.trim() || !requesterForm.jobTitle.trim()) {
      toast.error("Preencha nome e cargo do solicitante.");
      return;
    }
    upsertRequester.mutate({ companyId, data: requesterForm });
  };

  const handleOpenEvalDialog = (evaluator?: any) => {
    if (evaluator) {
      setEditingEvalId(evaluator.id);
      setEvalForm({
        name: evaluator.name,
        jobTitle: evaluator.jobTitle,
        education: evaluator.education,
        professionalRegistration: evaluator.professionalRegistration,
        specialization: evaluator.specialization || "",
        email: evaluator.email || "",
        phone: evaluator.phone || "",
      });
    } else {
      setEditingEvalId(null);
      setEvalForm(emptyEvaluator);
    }
    setEvalDialogOpen(true);
  };

  const handleSaveEvaluator = () => {
    if (
      !evalForm.name.trim() ||
      !evalForm.jobTitle.trim() ||
      !evalForm.education.trim() ||
      !evalForm.professionalRegistration.trim()
    ) {
      toast.error("Preencha todos os campos obrigatórios.");
      return;
    }
    const payload = {
      ...evalForm,
      specialization: evalForm.specialization || null,
      email: evalForm.email || null,
      phone: evalForm.phone || null,
    };
    if (editingEvalId) {
      updateEvaluator.mutate({ evaluatorId: editingEvalId, data: payload });
    } else {
      createEvaluator.mutate({ companyId, data: payload });
    }
  };

  const handleDeleteEvaluator = () => {
    if (deleteEvalId) {
      deleteEvaluator.mutate({ evaluatorId: deleteEvalId });
    }
  };

  const handleGenerateDocument = () => {
    generateDocument.mutate({ companyId });
    // Switch to the documents tab to show progress
    setActiveTab("documentos");
  };

  const handleDownload = async (docId: number) => {
    try {
      const result = await utils.documents.getDownloadUrl.fetch({ documentId: docId });
      if (result?.url) {
        const link = document.createElement("a");
        link.href = result.url;
        link.download = result.fileName || "documento.pdf";
        link.target = "_blank";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    } catch (err: any) {
      toast.error(err?.message || "Erro ao baixar documento.");
    }
  };

  const canEditRequester = ctx?.canEditRequester ?? false;
  const canEditEvaluators = ctx?.canEditEvaluators ?? false;

  // Format helpers
  const formatCnpj = (cnpj: string | null) => {
    if (!cnpj) return "Não informado";
    const clean = cnpj.replace(/\D/g, "");
    if (clean.length !== 14) return cnpj;
    return clean.replace(
      /^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/,
      "$1.$2.$3/$4-$5"
    );
  };

  const formatAddress = (c: any) => {
    const parts = [c.logradouro, c.numero, c.complemento, c.bairro, c.cidade, c.estado]
      .filter(Boolean);
    if (parts.length === 0) return "Não informado";
    let addr = parts.join(", ");
    if (c.cep) addr += ` — CEP ${c.cep}`;
    return addr;
  };

  // ============================================================
  // Render
  // ============================================================

  if (isLoading) {
    return (
      <SalviaLayout title="Documentos">
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-6 h-6 animate-spin text-teal-600 mr-2" />
          <span className="text-slate-500">Carregando contexto...</span>
        </div>
      </SalviaLayout>
    );
  }

  if (error || !ctx) {
    return (
      <SalviaLayout title="Documentos">
        <div className="p-8 max-w-3xl mx-auto text-center">
          <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
          <h2 className="text-lg font-semibold text-slate-800 mb-2">Erro ao carregar contexto</h2>
          <p className="text-sm text-slate-500 mb-4">
            {(error as any)?.message || "Empresa não encontrada ou sem permissão."}
          </p>
          <Button variant="outline" onClick={() => setLocation("/documents")}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Voltar
          </Button>
        </div>
      </SalviaLayout>
    );
  }

  const { company, orgStructure, totalVidas, statusSummary, requester, evaluators, currentCampaign, hasScope } = ctx;

  // Group orgStructure by sector
  const sectorMap = new Map<string, typeof orgStructure>();
  orgStructure.forEach((item: any) => {
    const key = item.sector || "Sem setor";
    if (!sectorMap.has(key)) sectorMap.set(key, []);
    sectorMap.get(key)!.push(item);
  });

  return (
    <SalviaLayout title="Documentos">
      <div className="p-6 lg:p-8 max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          <Button variant="ghost" size="sm" onClick={() => setLocation("/documents")}>
            <ArrowLeft className="w-4 h-4 mr-1" />
            Voltar
          </Button>
          <div className="h-6 w-px bg-slate-200" />
          <h1 className="text-xl font-bold text-slate-900">
            Documento — {company.name}
          </h1>
          {canGenerateDoc && (
            <div className="ml-auto">
              <Button
                onClick={handleGenerateDocument}
                disabled={generateDocument.isPending || !hasScope}
                className="bg-teal-600 hover:bg-teal-700 text-white"
                title={!hasScope ? "Nenhuma campanha com escopo disponível" : ""}
              >
                {generateDocument.isPending ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <FileText className="w-4 h-4 mr-2" />
                )}
                Gerar Documento
              </Button>
            </div>
          )}
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className={`grid w-full mb-6 ${canAccessGeneratedDocuments ? "grid-cols-2" : "grid-cols-1"}`}>
            <TabsTrigger value="contexto" className="gap-2">
              <Building2 className="w-4 h-4" />
              Contexto
            </TabsTrigger>
            {canAccessGeneratedDocuments && (
              <TabsTrigger value="documentos" className="gap-2">
                <FileText className="w-4 h-4" />
                Documentos Gerados
                {generatedDocs && generatedDocs.length > 0 && (
                  <Badge variant="secondary" className="ml-1 text-xs px-1.5 py-0">
                    {generatedDocs.length}
                  </Badge>
                )}
              </TabsTrigger>
            )}
          </TabsList>

          <TabsContent value="contexto">
          <div className="space-y-8">
          {/* ============================================================ */}
          {/* SEÇÃO 1: DADOS CADASTRAIS (read-only) */}
          {/* ============================================================ */}
          <section>
            <div className="flex items-center gap-2 mb-4">
              <Building2 className="w-5 h-5 text-teal-600" />
              <h2 className="text-lg font-semibold text-slate-900">
                1. Dados Cadastrais da Empresa
              </h2>
              <Badge variant="outline" className="text-xs bg-slate-50 text-slate-500 border-slate-200 ml-2">
                Somente leitura
              </Badge>
            </div>

            <div className="bg-white border border-slate-200 rounded-lg p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4">
                <InfoField label="Razão Social" value={company.razaoSocial} />
                <InfoField label="Nome Fantasia" value={company.nomeFantasia || company.name} />
                <InfoField label="CNPJ" value={formatCnpj(company.cnpj)} />
                <InfoField label="CNAE" value={company.cnae} />
                <InfoField label="Atividade Principal" value={company.atividadePrincipal} />
                <InfoField label="Grau de Risco" value={company.grauRisco} />
                <InfoField label="Endereço" value={formatAddress(company)} className="md:col-span-2" />
                <InfoField label="Email" value={company.email} />
                <InfoField label="Telefone" value={company.phone} />
                <InfoField label="Responsável" value={company.responsibleName} />
                <InfoField label="Total de Funcionários" value={company.employeesCount ? String(company.employeesCount) : null} />
              </div>

              <div className="mt-4 flex items-start gap-2 p-3 bg-blue-50 border border-blue-100 rounded-lg">
                <Info className="w-4 h-4 text-blue-500 mt-0.5 flex-shrink-0" />
                <p className="text-xs text-blue-700">
                  Para alterar dados cadastrais, acesse a ficha do cliente em{" "}
                  <button
                    className="underline font-medium"
                    onClick={() => setLocation(`/clients/${companyId}`)}
                  >
                    Clientes → {company.name}
                  </button>.
                </p>
              </div>
            </div>
          </section>

          {/* ============================================================ */}
          {/* SEÇÃO 2: ESTRUTURA ORGANIZACIONAL (read-only, da importação) */}
          {/* ============================================================ */}
          <section>
            <div className="flex items-center gap-2 mb-4">
              <Users className="w-5 h-5 text-teal-600" />
              <h2 className="text-lg font-semibold text-slate-900">
                2. Estrutura Organizacional
              </h2>
              {currentCampaign ? (
                <Badge variant="outline" className="text-xs bg-blue-50 text-blue-700 border-blue-200 ml-2">
                  Campanha #{currentCampaign.id} · {currentCampaign.status === "ACTIVE" ? "Ativa" : "Encerrada"}
                </Badge>
              ) : (
                <Badge variant="outline" className="text-xs bg-slate-50 text-slate-500 border-slate-200 ml-2">
                  Sem campanha
                </Badge>
              )}
              {totalVidas > 0 && (
                <Badge variant="outline" className="text-xs bg-emerald-50 text-emerald-700 border-emerald-200 ml-auto">
                  {totalVidas} vidas · {orgStructure.length} cargos · {sectorMap.size} setores
                </Badge>
              )}
            </div>

            {/* Resumo por status */}
            {statusSummary && statusSummary.total > 0 && (
              <div className="flex flex-wrap gap-2 mb-3">
                {statusSummary.concluida > 0 && (
                  <Badge variant="outline" className="text-xs bg-emerald-50 text-emerald-700 border-emerald-200">
                    {statusSummary.concluida} concluída{statusSummary.concluida > 1 ? "s" : ""}
                  </Badge>
                )}
                {statusSummary.coletaEmAndamento > 0 && (
                  <Badge variant="outline" className="text-xs bg-blue-50 text-blue-700 border-blue-200">
                    {statusSummary.coletaEmAndamento} em coleta
                  </Badge>
                )}
                {statusSummary.coletaEncerrada > 0 && (
                  <Badge variant="outline" className="text-xs bg-indigo-50 text-indigo-700 border-indigo-200">
                    {statusSummary.coletaEncerrada} coleta encerrada
                  </Badge>
                )}
                {statusSummary.pendente > 0 && (
                  <Badge variant="outline" className="text-xs bg-amber-50 text-amber-700 border-amber-200">
                    {statusSummary.pendente} pendente{statusSummary.pendente > 1 ? "s" : ""}
                  </Badge>
                )}
                {statusSummary.emProcessamento > 0 && (
                  <Badge variant="outline" className="text-xs bg-purple-50 text-purple-700 border-purple-200">
                    {statusSummary.emProcessamento} em processamento
                  </Badge>
                )}
                {statusSummary.bloqueada > 0 && (
                  <Badge variant="outline" className="text-xs bg-red-50 text-red-700 border-red-200">
                    {statusSummary.bloqueada} bloqueada{statusSummary.bloqueada > 1 ? "s" : ""}
                  </Badge>
                )}
                {statusSummary.semAvaliacao > 0 && (
                  <Badge variant="outline" className="text-xs bg-gray-50 text-gray-500 border-gray-200">
                    {statusSummary.semAvaliacao} sem avaliação
                  </Badge>
                )}
              </div>
            )}

            <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
              {orgStructure.length === 0 ? (
                <div className="p-8 text-center">
                  <AlertCircle className="w-8 h-8 text-amber-400 mx-auto mb-3" />
                  <p className="text-sm text-slate-600 font-medium mb-1">
                    {!currentCampaign
                      ? "Nenhuma campanha com escopo encontrada"
                      : "Nenhum cargo importado para a campanha corrente"}
                  </p>
                  <p className="text-xs text-slate-400">
                    {!currentCampaign
                      ? "Crie uma campanha e importe a planilha de cargos para iniciar."
                      : (
                        <>
                          Importe a planilha de cargos na seção{" "}
                          <button
                            className="underline text-teal-600"
                            onClick={() => setLocation("/importacao")}
                          >
                            Importação
                          </button>{" "}
                          para preencher a estrutura organizacional.
                        </>
                      )}
                  </p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-slate-50 border-b border-slate-200">
                        <th className="text-left p-3 font-medium text-slate-600">Setor</th>
                        <th className="text-left p-3 font-medium text-slate-600">Cargo</th>
                        <th className="text-center p-3 font-medium text-slate-600">Vidas</th>
                        <th className="text-left p-3 font-medium text-slate-600">Status</th>
                        <th className="text-left p-3 font-medium text-slate-600">Descrição</th>
                      </tr>
                    </thead>
                    <tbody>
                      {orgStructure.map((item: any, idx: number) => (
                        <tr key={idx} className={idx % 2 === 0 ? "bg-white" : "bg-slate-50/50"}>
                          <td className="p-3 text-slate-700 capitalize">{item.sector}</td>
                          <td className="p-3 text-slate-700 capitalize font-medium">{item.jobTitle}</td>
                          <td className="p-3 text-center text-slate-700">{item.quantity}</td>
                          <td className="p-3">
                            <EvaluationStatusBadge status={item.evaluationStatus} />
                          </td>
                          <td className="p-3 text-slate-500 text-xs max-w-[300px] truncate">
                            {item.jobDescription || "—"}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr className="bg-slate-100 border-t border-slate-200 font-semibold">
                        <td className="p-3 text-slate-700">Total</td>
                        <td className="p-3 text-slate-700">{orgStructure.length} cargos</td>
                        <td className="p-3 text-center text-slate-700">{totalVidas}</td>
                        <td className="p-3"></td>
                        <td className="p-3"></td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              )}
            </div>
          </section>

          {/* ============================================================ */}
          {/* SEÇÃO 3: SOLICITANTE (CRUD) */}
          {/* ============================================================ */}
          <section>
            <div className="flex items-center gap-2 mb-4">
              <UserCheck className="w-5 h-5 text-teal-600" />
              <h2 className="text-lg font-semibold text-slate-900">
                3. Solicitante da Avaliação
              </h2>
              {canEditRequester && !requester && !requesterEditing && (
                <Button
                  size="sm"
                  variant="outline"
                  className="ml-auto"
                  onClick={() => {
                    setRequesterForm(emptyRequester);
                    setRequesterEditing(true);
                  }}
                >
                  <Plus className="w-3.5 h-3.5 mr-1" />
                  Adicionar
                </Button>
              )}
            </div>

            <div className="bg-white border border-slate-200 rounded-lg p-6">
              {!requester && !requesterEditing ? (
                <div className="text-center py-4">
                  <AlertCircle className="w-8 h-8 text-amber-400 mx-auto mb-3" />
                  <p className="text-sm text-slate-600 font-medium">
                    Nenhum solicitante cadastrado
                  </p>
                  <p className="text-xs text-slate-400 mt-1">
                    {canEditRequester
                      ? "Clique em Adicionar para cadastrar o solicitante."
                      : "Seu perfil pode consultar este solicitante, mas não editar esse cadastro."}
                  </p>
                </div>
              ) : requesterEditing ? (
                <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label>Nome do Solicitante *</Label>
                      <Input
                        value={requesterForm.name}
                        onChange={(e) =>
                          setRequesterForm((f) => ({ ...f, name: e.target.value }))
                        }
                        placeholder="Ex: João da Silva"
                      />
                    </div>
                    <div>
                      <Label>Cargo *</Label>
                      <Input
                        value={requesterForm.jobTitle}
                        onChange={(e) =>
                          setRequesterForm((f) => ({ ...f, jobTitle: e.target.value }))
                        }
                        placeholder="Ex: Diretor de RH"
                      />
                    </div>
                  </div>
                  <div className="flex gap-2 justify-end">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setRequesterEditing(false);
                        if (requester) {
                          setRequesterForm({
                            name: requester.name,
                            jobTitle: requester.jobTitle,
                          });
                        }
                      }}
                    >
                      <X className="w-3.5 h-3.5 mr-1" />
                      Cancelar
                    </Button>
                    <Button
                      size="sm"
                      onClick={handleSaveRequester}
                      disabled={upsertRequester.isPending}
                    >
                      {upsertRequester.isPending ? (
                        <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />
                      ) : (
                        <Save className="w-3.5 h-3.5 mr-1" />
                      )}
                      Salvar
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="p-2 bg-emerald-50 rounded-lg">
                      <CheckCircle2 className="w-5 h-5 text-emerald-600" />
                    </div>
                    <div>
                      <p className="font-semibold text-slate-900">{requester!.name}</p>
                      <p className="text-sm text-slate-500">{requester!.jobTitle}</p>
                    </div>
                  </div>
                  {canEditRequester && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setRequesterEditing(true)}
                    >
                      <Pencil className="w-3.5 h-3.5 mr-1" />
                      Editar
                    </Button>
                  )}
                </div>
              )}
            </div>
          </section>

          {/* ============================================================ */}
          {/* SEÇÃO 4: AVALIADORES (CRUD) */}
          {/* ============================================================ */}
          <section>
            <div className="flex items-center gap-2 mb-4">
              <ClipboardList className="w-5 h-5 text-teal-600" />
              <h2 className="text-lg font-semibold text-slate-900">
                4. Avaliadores / Responsáveis Técnicos
              </h2>
              {canEditEvaluators && (
                <Button
                  size="sm"
                  variant="outline"
                  className="ml-auto"
                  onClick={() => handleOpenEvalDialog()}
                >
                  <Plus className="w-3.5 h-3.5 mr-1" />
                  Adicionar
                </Button>
              )}
            </div>

            <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
              {evaluators.length === 0 ? (
                <div className="p-8 text-center">
                  <AlertCircle className="w-8 h-8 text-amber-400 mx-auto mb-3" />
                  <p className="text-sm text-slate-600 font-medium">
                    Nenhum avaliador cadastrado
                  </p>
                  <p className="text-xs text-slate-400 mt-1">
                    {canEditEvaluators
                      ? "Clique em Adicionar para cadastrar avaliadores."
                      : "Apenas o papel estrutural ADM pode cadastrar os avaliadores."}
                  </p>
                </div>
              ) : (
                <div className="divide-y divide-slate-100">
                  {evaluators.map((ev: any) => (
                    <div key={ev.id} className="p-4 flex items-start justify-between hover:bg-slate-50/50">
                      <div className="space-y-1">
                        <p className="font-semibold text-slate-900">{ev.name}</p>
                        <p className="text-sm text-slate-600">{ev.jobTitle}</p>
                        <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-500">
                          <span>Formação: {ev.education}</span>
                          <span>Registro: {ev.professionalRegistration}</span>
                          {ev.specialization && <span>Especialização: {ev.specialization}</span>}
                          {ev.email && <span>Email: {ev.email}</span>}
                          {ev.phone && <span>Tel: {ev.phone}</span>}
                        </div>
                      </div>
                      {canEditEvaluators && (
                        <div className="flex gap-1 flex-shrink-0 ml-4">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleOpenEvalDialog(ev)}
                          >
                            <Pencil className="w-3.5 h-3.5" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-red-500 hover:text-red-700 hover:bg-red-50"
                            onClick={() => setDeleteEvalId(ev.id)}
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </section>
        </div>
          </TabsContent>

          {canAccessGeneratedDocuments && (
            <TabsContent value="documentos">
              <GeneratedDocumentsTab
                docs={generatedDocs || []}
                isLoading={isLoadingDocs}
                onDownload={handleDownload}
                onRefresh={() => refetchDocs()}
                onGenerate={handleGenerateDocument}
                isGenerating={generateDocument.isPending}
                canGenerate={canGenerateDoc}
              />
            </TabsContent>
          )}
        </Tabs>

        {/* ============================================================ */}
        {/* DIALOG: Criar/Editar Avaliador */}
        {/* ============================================================ */}
        <Dialog open={evalDialogOpen} onOpenChange={setEvalDialogOpen}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>
                {editingEvalId ? "Editar Avaliador" : "Novo Avaliador"}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-2">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>Nome *</Label>
                  <Input
                    value={evalForm.name}
                    onChange={(e) =>
                      setEvalForm((f) => ({ ...f, name: e.target.value }))
                    }
                    placeholder="Ex: Dr. Carlos Silva"
                  />
                </div>
                <div>
                  <Label>Cargo / Função *</Label>
                  <Input
                    value={evalForm.jobTitle}
                    onChange={(e) =>
                      setEvalForm((f) => ({ ...f, jobTitle: e.target.value }))
                    }
                    placeholder="Ex: Médico do Trabalho"
                  />
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>Formação *</Label>
                  <Input
                    value={evalForm.education}
                    onChange={(e) =>
                      setEvalForm((f) => ({ ...f, education: e.target.value }))
                    }
                    placeholder="Ex: Medicina"
                  />
                </div>
                <div>
                  <Label>Registro Profissional *</Label>
                  <Input
                    value={evalForm.professionalRegistration}
                    onChange={(e) =>
                      setEvalForm((f) => ({
                        ...f,
                        professionalRegistration: e.target.value,
                      }))
                    }
                    placeholder="Ex: CRM 12345/SP"
                  />
                </div>
              </div>
              <div>
                <Label>Especialização</Label>
                <Input
                  value={evalForm.specialization || ""}
                  onChange={(e) =>
                    setEvalForm((f) => ({ ...f, specialization: e.target.value }))
                  }
                  placeholder="Ex: Ergonomia"
                />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>Email</Label>
                  <Input
                    type="email"
                    value={evalForm.email || ""}
                    onChange={(e) =>
                      setEvalForm((f) => ({ ...f, email: e.target.value }))
                    }
                    placeholder="email@exemplo.com"
                  />
                </div>
                <div>
                  <Label>Telefone</Label>
                  <Input
                    value={evalForm.phone || ""}
                    onChange={(e) =>
                      setEvalForm((f) => ({ ...f, phone: e.target.value }))
                    }
                    placeholder="(11) 99999-9999"
                  />
                </div>
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <Button
                  variant="outline"
                  onClick={() => setEvalDialogOpen(false)}
                >
                  Cancelar
                </Button>
                <Button
                  onClick={handleSaveEvaluator}
                  disabled={
                    createEvaluator.isPending || updateEvaluator.isPending
                  }
                >
                  {(createEvaluator.isPending || updateEvaluator.isPending) ? (
                    <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                  ) : (
                    <Save className="w-4 h-4 mr-1" />
                  )}
                  Salvar
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* ============================================================ */}
        {/* ALERT DIALOG: Confirmar exclusão de avaliador */}
        {/* ============================================================ */}
        <AlertDialog open={!!deleteEvalId} onOpenChange={(open) => !open && setDeleteEvalId(null)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Excluir avaliador?</AlertDialogTitle>
              <AlertDialogDescription>
                Esta ação não pode ser desfeita. O avaliador será removido permanentemente.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancelar</AlertDialogCancel>
              <AlertDialogAction
                className="bg-red-600 hover:bg-red-700"
                onClick={handleDeleteEvaluator}
              >
                {deleteEvaluator.isPending ? (
                  <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                ) : (
                  <Trash2 className="w-4 h-4 mr-1" />
                )}
                Excluir
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </SalviaLayout>
  );
}

// ============================================================
// Sub-components
// ============================================================

function InfoField({
  label,
  value,
  className = "",
}: {
  label: string;
  value: string | null | undefined;
  className?: string;
}) {
  return (
    <div className={className}>
      <p className="text-xs text-slate-400 mb-0.5">{label}</p>
      <p className="text-sm text-slate-800 font-medium">
        {value || <span className="text-slate-400 italic">Não informado</span>}
      </p>
    </div>
  );
}

// ============================================================
// Generated Documents Tab
// ============================================================

interface GeneratedDocumentsTabProps {
  docs: any[];
  isLoading: boolean;
  onDownload: (docId: number) => void;
  canGenerate?: boolean;
  onRefresh: () => void;
  onGenerate: () => void;
  isGenerating: boolean;
}

function GeneratedDocumentsTab({
  docs,
  isLoading,
  onDownload,
  canGenerate = true,
  onRefresh,
  onGenerate,
  isGenerating,
}: GeneratedDocumentsTabProps) {
  const formatDate = (date: Date | string | null) => {
    if (!date) return "—";
    return new Date(date).toLocaleDateString("pt-BR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const statusConfig: Record<string, { icon: React.ReactNode; label: string; className: string }> = {
    processing: {
      icon: <Loader2 className="w-4 h-4 animate-spin" />,
      label: "Gerando...",
      className: "bg-blue-50 text-blue-700 border-blue-200",
    },
    ready: {
      icon: <FileCheck className="w-4 h-4" />,
      label: "Pronto",
      className: "bg-emerald-50 text-emerald-700 border-emerald-200",
    },
    failed: {
      icon: <XCircle className="w-4 h-4" />,
      label: "Falhou",
      className: "bg-red-50 text-red-700 border-red-200",
    },
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="w-6 h-6 animate-spin text-teal-600 mr-2" />
        <span className="text-slate-500">Carregando documentos...</span>
      </div>
    );
  }

  if (docs.length === 0) {
    return (
      <div className="bg-white border border-slate-200 rounded-lg p-12 text-center">
        <FileText className="w-12 h-12 text-slate-300 mx-auto mb-4" />
        <h3 className="text-lg font-semibold text-slate-700 mb-2">
          Nenhum documento gerado
        </h3>
        <p className="text-sm text-slate-500 mb-6 max-w-md mx-auto">
          {canGenerate
            ? 'Clique em "Gerar Documento" para criar o primeiro inventário de riscos psicossociais desta empresa.'
            : 'Nenhum documento foi gerado para esta empresa ainda.'}
        </p>
        {canGenerate && (
          <Button
            onClick={onGenerate}
            disabled={isGenerating}
            className="bg-teal-600 hover:bg-teal-700 text-white"
          >
            {isGenerating ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <FileText className="w-4 h-4 mr-2" />
            )}
            Gerar Documento
          </Button>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header com refresh */}
      <div className="flex items-center justify-between">
        <p className="text-sm text-slate-500">
          {docs.length} documento{docs.length !== 1 ? "s" : ""} gerado{docs.length !== 1 ? "s" : ""}
        </p>
        <Button variant="ghost" size="sm" onClick={onRefresh}>
          <RefreshCw className="w-3.5 h-3.5 mr-1" />
          Atualizar
        </Button>
      </div>

      {/* Lista de documentos */}
      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden divide-y divide-slate-100">
        {docs.map((doc: any) => {
          const config = statusConfig[doc.status] || statusConfig.failed;
          return (
            <div
              key={doc.id}
              className="p-4 flex items-center gap-4 hover:bg-slate-50/50"
            >
              {/* Ícone de status */}
              <div
                className={`p-2 rounded-lg border ${config.className}`}
              >
                {config.icon}
              </div>

              {/* Info principal */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <p className="font-semibold text-slate-900 text-sm">
                    Inventário de Riscos Psicossociais
                  </p>
                  <Badge variant="outline" className="text-xs">
                    v{doc.versionNumber}
                  </Badge>
                  <Badge
                    variant="outline"
                    className={`text-xs ${config.className}`}
                  >
                    {config.label}
                  </Badge>
                </div>
                <div className="flex items-center gap-3 mt-1 text-xs text-slate-500">
                  <span className="flex items-center gap-1">
                    <Clock className="w-3 h-3" />
                    {formatDate(doc.createdAt)}
                  </span>
                  {doc.campaignId && (
                    <span>Campanha #{doc.campaignId}</span>
                  )}
                  <span>Por: {doc.generatedByName}</span>
                  {doc.status === "processing" && doc.processingStartedAt && (
                    <span className="text-blue-600 animate-pulse">
                      Processando...
                    </span>
                  )}
                </div>
                {doc.status === "failed" && (
                  <p className="text-xs text-red-500 mt-1">
                    Erro ao gerar documento. Clique em "Tentar novamente" para uma nova tentativa.
                  </p>
                )}
              </div>

              {/* Ações */}
              <div className="flex-shrink-0">
                {doc.status === "ready" && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => onDownload(doc.id)}
                    className="gap-1.5"
                  >
                    <Download className="w-3.5 h-3.5" />
                    Baixar PDF
                  </Button>
                )}
                {doc.status === "processing" && (
                  <Badge variant="outline" className="text-xs bg-blue-50 text-blue-600 border-blue-200">
                    <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                    Aguarde
                  </Badge>
                )}
                {doc.status === "failed" && canGenerate && (
                  <Button
                    size="sm"
                    variant="outline"
                    className="text-red-600 border-red-200 hover:bg-red-50"
                    onClick={onGenerate}
                    disabled={isGenerating}
                  >
                    <RefreshCw className="w-3.5 h-3.5 mr-1" />
                    Tentar novamente
                  </Button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
